import type { InboxRequestedTeam } from '@/app/(home)/inbox/types';
import { Check, MessageSquare, Users, X } from 'lucide-react';

import { REVIEWER_BADGE_CLASS } from './reviewer-badge';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const REVIEW_STATES = {
  APPROVED: 'approved',
  COMMENTED: 'commented',
  CHANGES_REQUESTED: 'requested changes',
  DISMISSED: 'dismissed',
  WAITING: 'waiting for review',
} as const;

export type ReviewState = (typeof REVIEW_STATES)[keyof typeof REVIEW_STATES];

/** A reviewer as the avatar stack draws them. */
export type ReviewerAvatar = {
  login: string;
  avatarUrl: string | null;
  status: ReviewState;
  isCurrentlyRequested: boolean;
};

/**
 * Corner badge carrying a human reviewer's latest review state, in the same
 * style as cubic's badge and in grey like it: check for approved, X for
 * requested changes, comment bubble for commented. Waiting/dismissed show no
 * badge (the dark overlay already marks currently-requested reviewers).
 */
function HumanReviewerBadge({ status }: { status: ReviewState }) {
  switch (status) {
    case REVIEW_STATES.APPROVED:
      return (
        <span
          className={REVIEWER_BADGE_CLASS}
          aria-hidden="true"
          data-testid="reviewer-badge-approved"
        >
          <Check className="size-2 text-zinc-300" />
        </span>
      );
    case REVIEW_STATES.CHANGES_REQUESTED:
      return (
        <span
          className={REVIEWER_BADGE_CLASS}
          aria-hidden="true"
          data-testid="reviewer-badge-changes"
        >
          <X className="size-2 text-zinc-300" />
        </span>
      );
    case REVIEW_STATES.COMMENTED:
      return (
        <span className={REVIEWER_BADGE_CLASS} aria-hidden="true">
          <MessageSquare className="size-2 text-zinc-300" />
        </span>
      );
    default:
      return null;
  }
}

/**
 * The reviewers' avatars in a stack, each with a grey corner badge for their latest review and a
 * dark overlay while their review is requested, then the requested teams. The tooltip groups them
 * by review status; a re-requested reviewer appears under their status and under waiting.
 */
export function ReviewerAvatarStack({
  reviewers: displayedReviewers,
  teams: displayedTeams,
}: {
  reviewers: ReviewerAvatar[];
  // Teams have no review state of their own: a requested team is always waiting
  // (GitHub clears the request once a member reviews)
  teams: InboxRequestedTeam[];
}) {
  if (!displayedReviewers.length && !displayedTeams.length) {
    return null;
  }

  /**
   * Group reviewers by their status for the tooltip display
   * Special handling: If a reviewer has already reviewed AND is currently requested again,
   * they appear in both their review status group AND the "waiting for review" group
   */
  const groupedReviewers = displayedReviewers.reduce(
    (acc, reviewer) => {
      const status = reviewer.status;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(reviewer);

      if (reviewer.isCurrentlyRequested && status !== REVIEW_STATES.WAITING) {
        if (!acc[REVIEW_STATES.WAITING]) {
          acc[REVIEW_STATES.WAITING] = [];
        }
        acc[REVIEW_STATES.WAITING].push({
          ...reviewer,
          status: REVIEW_STATES.WAITING,
        });
      }

      return acc;
    },
    {} as Record<ReviewState, ReviewerAvatar[]>,
  );

  const statusOrder: ReviewState[] = [
    REVIEW_STATES.APPROVED,
    REVIEW_STATES.CHANGES_REQUESTED,
    REVIEW_STATES.DISMISSED,
    REVIEW_STATES.COMMENTED,
    REVIEW_STATES.WAITING,
  ];

  return (
    <TooltipProvider>
      <Tooltip delayDuration={50}>
        <TooltipTrigger asChild>
          <div className="flex items-center -space-x-1">
            {displayedReviewers.map((reviewer) => (
              // Wrapper span hosts the corner badge: the Avatar base class
              // applies overflow-hidden, which would clip it.
              <span
                key={reviewer.login}
                data-testid="human-reviewer-avatar"
                className="relative inline-flex size-5 shrink-0"
              >
                <Avatar className="relative inline-block size-5">
                  <AvatarImage
                    src={reviewer.avatarUrl ?? undefined}
                    alt={`${reviewer.login}'s avatar`}
                    loading="lazy"
                    priority={false}
                    width={20}
                    height={20}
                  />
                  <AvatarFallback className="bg-zinc-800 text-xs">
                    {(reviewer.login ?? '').slice(0, 1).toUpperCase() || 'N'}
                  </AvatarFallback>
                  {reviewer.isCurrentlyRequested && (
                    <div className="absolute inset-0 rounded-full bg-black/50" />
                  )}
                </Avatar>
                <HumanReviewerBadge status={reviewer.status} />
              </span>
            ))}
            {displayedTeams.map((team) => (
              <Avatar
                key={`team-${team.slug ?? team.name}`}
                className="relative inline-block size-5"
              >
                <AvatarImage
                  src={team.avatar_url ?? undefined}
                  alt={`${team.name} team avatar`}
                  loading="lazy"
                  priority={false}
                  width={20}
                  height={20}
                />
                <AvatarFallback className="bg-zinc-800">
                  <Users className="size-3 text-zinc-400" />
                </AvatarFallback>
                <div className="absolute inset-0 rounded-full bg-black/50" />
              </Avatar>
            ))}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="cursor-default space-y-2 p-1">
            <div className="space-y-4">
              {statusOrder.map((status) => {
                const reviewers = groupedReviewers[status] ?? [];
                // Requested teams are always waiting for review
                const teamsInGroup = status === REVIEW_STATES.WAITING ? displayedTeams : [];
                if (!reviewers.length && !teamsInGroup.length) {
                  return null;
                }

                return (
                  <div key={status} className="space-y-2">
                    <p className="text-xs font-medium text-zinc-400">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </p>
                    {teamsInGroup.map((team) => (
                      <div
                        key={`team-${team.slug ?? team.name}`}
                        className="flex items-center gap-2"
                      >
                        <Avatar className="relative size-4">
                          <AvatarImage
                            src={team.avatar_url ?? undefined}
                            alt={`${team.name} team avatar`}
                            loading="lazy"
                            priority={false}
                            width={16}
                            height={16}
                          />
                          <AvatarFallback>
                            <Users className="size-2.5 text-zinc-400" />
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{team.name}</span>
                      </div>
                    ))}
                    {reviewers.map((reviewer) => (
                      <div key={`${reviewer.login}-${status}`} className="flex items-center gap-2">
                        <Avatar className="relative size-4">
                          <AvatarImage
                            src={reviewer.avatarUrl ?? undefined}
                            alt={`${reviewer.login}'s avatar`}
                            loading="lazy"
                            priority={false}
                            width={16}
                            height={16}
                          />
                          <AvatarFallback className="text-sm">
                            {(reviewer.login ?? '').slice(0, 1).toUpperCase() || 'N'}
                          </AvatarFallback>
                          {reviewer.isCurrentlyRequested && (
                            <div className="absolute inset-0 rounded-full bg-black/50" />
                          )}
                        </Avatar>
                        <span className="text-sm">{reviewer.login}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
